/**
 * Reusable SEO component for per-route meta tags.
 *
 * Manages:
 *   - <title>, <meta description>
 *   - Open Graph and Twitter Cards
 *   - hreflang alternates (ca / es / fr / en + x-default) auto-generated
 *     from the current pathname
 *   - JSON-LD structured data (GolfCourse on the homepage by default,
 *     custom schemas on other pages via the `jsonLd` prop)
 *
 * Usage:
 *   <SEO
 *     title="Restaurant del Club"
 *     description="Gastronomia de proximitat amb vistes als Pirineus"
 *     image="https://.../restaurant-cover.jpg"
 *     type="article"
 *     jsonLd={customRestaurantSchema}      // optional, overrides default
 *   />
 */
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://aravellgolfclub.com";
const SITE_NAME = "UGOLF ARAVELL Andorra";
const VALID_LANGS = ["ca", "es", "fr", "en"];
const DEFAULT_LANG = "ca";

// ISO locales for Open Graph (og:locale must be in format <lang>_<COUNTRY>)
const OG_LOCALES = {
  ca: "ca_ES",
  es: "es_ES",
  fr: "fr_FR",
  en: "en_GB",
};

const DEFAULT_TITLE = "UGOLF ARAVELL Andorra - Camp de Golf als Pirineus";
const DEFAULT_DESCRIPTION =
  "UGOLF ARAVELL Andorra - Camp de golf de 18 forats dissenyat per José María Olazábal als Pirineus catalans. Reserva el teu green fee, descobreix el restaurant i les nostres instal·lacions.";

const DEFAULT_OG_IMAGE =
  "https://customer-assets.emergentagent.com/job_aravell-staging/artifacts/xf3js5gj_ARAVELL_CADI_HOME_1920x900_USER_EXACT%20%281%29.jpeg";

// ---------- helpers ----------

/**
 * Given the current pathname, returns:
 *   - currentLang: the language detected from the URL (ca by default)
 *   - slug: the path without the language prefix (no leading slash)
 */
function parsePath(pathname) {
  const segments = (pathname || "/").split("/").filter(Boolean);
  if (segments.length > 0 && VALID_LANGS.includes(segments[0])) {
    return {
      currentLang: segments[0],
      slug: segments.slice(1).join("/"),
    };
  }
  return { currentLang: DEFAULT_LANG, slug: segments.join("/") };
}

/**
 * Build the URL for a given language and slug.
 *   buildLangUrl('ca', 'restaurant') -> https://aravellgolfclub.com/restaurant
 *   buildLangUrl('es', 'restaurant') -> https://aravellgolfclub.com/es/restaurant
 *   buildLangUrl('en', '')           -> https://aravellgolfclub.com/en
 *   buildLangUrl('ca', '')           -> https://aravellgolfclub.com/
 */
function buildLangUrl(lang, slug) {
  if (lang === DEFAULT_LANG) {
    return slug ? `${SITE_URL}/${slug}` : `${SITE_URL}/`;
  }
  return slug ? `${SITE_URL}/${lang}/${slug}` : `${SITE_URL}/${lang}`;
}

// ---------- default structured data ----------

/**
 * GolfCourse schema for UGOLF ARAVELL Andorra.
 * Only emitted on the homepage by default. Other pages should pass
 * their own `jsonLd` prop (or `null` to skip).
 */
const DEFAULT_GOLF_COURSE_LD = {
  "@context": "https://schema.org",
  "@type": "GolfCourse",
  "@id": `${SITE_URL}/#golfcourse`,
  name: "UGOLF ARAVELL Andorra",
  alternateName: "Aravell Golf Club",
  description:
    "Camp de golf de 18 forats als Pirineus catalans, dissenyat per José María Olazábal. Ofereix green fees, restaurant, driving range i programes d'iniciació.",
  url: SITE_URL,
  logo: `${SITE_URL}/favicon.ico`,
  image: DEFAULT_OG_IMAGE,
  telephone: "+34 661 19 40 23",
  email: "secretaria@aravellgolfclub.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "AV. del Cadí 6",
    postalCode: "25712",
    addressLocality: "Aravell",
    addressRegion: "Lleida",
    addressCountry: "ES",
  },
  // Exact coordinates of UGOLF ARAVELL Andorra (provided by the club).
  geo: {
    "@type": "GeoCoordinates",
    latitude: 42.36092051966573,
    longitude: 1.4071743197223372,
  },
  numberOfHoles: 18,
  priceRange: "€€",
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "09:00",
      closes: "18:30",
      validFrom: "2026-03-01",
      validThrough: "2026-03-31",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: "08:00",
      closes: "20:00",
      validFrom: "2026-04-01",
      validThrough: "2026-10-31",
    },
  ],
  sameAs: [
    "https://www.instagram.com/ugolfaravell/",
    "https://www.facebook.com/ugolfaravell/",
  ],
};

/**
 * Pick the right value from a localized prop.
 * If `value` is a plain string, return it unchanged.
 * If `value` is an object like { ca, es, fr, en }, return the entry for
 * `lang` (falling back to the default language and then to any other lang).
 */
function pickLocalized(value, lang) {
  if (value == null || typeof value === "string") return value;
  if (typeof value === "object") {
    return (
      value[lang] ||
      value[DEFAULT_LANG] ||
      Object.values(value).find(Boolean) ||
      null
    );
  }
  return value;
}

// ---------- component ----------

export const SEO = ({
  title,
  description,
  image,
  url,
  type = "website",
  twitterCard = "summary_large_image",
  jsonLd, // pass `null` to skip; omit to use the default on homepage only
}) => {
  const location = useLocation();
  const { currentLang, slug } = parsePath(location.pathname);

  const pageTitle = pickLocalized(title, currentLang) || DEFAULT_TITLE;
  const pageDescription = pickLocalized(description, currentLang) || DEFAULT_DESCRIPTION;
  const pageImage = image || DEFAULT_OG_IMAGE;
  const pageUrl = url || buildLangUrl(currentLang, slug);

  // Decide which JSON-LD to inject.
  //  - explicit prop wins (even `null` skips injection)
  //  - on the homepage, fall back to the GolfCourse schema
  //  - on other pages, nothing
  const isHomepage = !slug;
  let activeJsonLd;
  if (jsonLd !== undefined) {
    activeJsonLd = jsonLd;
  } else if (isHomepage) {
    activeJsonLd = DEFAULT_GOLF_COURSE_LD;
  } else {
    activeJsonLd = null;
  }

  return (
    <Helmet>
      {/* Primary tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Open Graph (Facebook, LinkedIn, WhatsApp, Telegram…) */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={OG_LOCALES[currentLang] || OG_LOCALES[DEFAULT_LANG]} />

      {/* Twitter Cards */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* hreflang alternates */}
      {VALID_LANGS.map((lang) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={buildLangUrl(lang, slug)}
        />
      ))}
      <link
        rel="alternate"
        hrefLang="x-default"
        href={buildLangUrl(DEFAULT_LANG, slug)}
      />

      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />

      {/* JSON-LD structured data */}
      {activeJsonLd ? (
        <script type="application/ld+json">
          {JSON.stringify(activeJsonLd)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default SEO;
